import { FocusEventHandler, KeyboardEvent } from 'react';

import type { BaseUIEvent } from '@base-ui/react/types';

import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropdown } from '@components/icons/dropdown';
import { IconFormIndicatorLocked } from '@components/icons/form-indicator-locked';
import { Tooltip } from '@components/tooltip';
import { useIsOverflowing } from '@hooks/use-is-overflowing';

import { useSelectTranslations } from './locale';
import {
  ClearButton,
  Control,
  ControlAnchor,
  DropdownButton,
  Indicators,
  Input,
  InputSizer,
  Prefix,
  PrefixContainer,
  ValueOverlay,
} from './styles';
import { EchoedSelectionInput } from './use-echoed-selection-input';

type SingleValueControlProps = {
  inputId: string;
  placeholder: string;
  prefix?: string;
  echo: EchoedSelectionInput;
  valueText?: string;
  hasSelection: boolean;
  isClearable: boolean;
  isDisabled: boolean;
  isSearchable?: boolean;
  isAutoFocused?: boolean;
  hasError: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onOpen?: () => void;
};

export const SingleValueControl = ({
  inputId,
  placeholder,
  prefix,
  echo,
  valueText,
  hasSelection,
  isClearable,
  isDisabled,
  isSearchable = true,
  isAutoFocused = false,
  hasError,
  onBlur,
  onFocus,
  onOpen,
}: SingleValueControlProps) => {
  const { t } = useSelectTranslations();
  const shouldShowOverlay = echo.overlay != null;
  const shouldShowPrefix = Boolean(prefix && hasSelection);
  const [overlayRef, isOverlayTruncated] = useIsOverflowing('horizontal', 'subtree');
  const fullLabel = shouldShowOverlay && isOverlayTruncated && !echo.isOpen ? valueText : undefined;

  const handleKeyDown = (event: BaseUIEvent<KeyboardEvent<HTMLInputElement>>) => {
    // A non-searchable select is a select-only combobox, where Space opens the
    // list and picks the highlighted option, while base-ui treats Space as text.
    if (!isSearchable && event.key === ' ') {
      event.preventDefault();
      event.preventBaseUIHandler();
      if (!echo.isOpen) {
        onOpen?.();
      } else {
        document
          .getElementById(event.currentTarget.getAttribute('aria-activedescendant') ?? '')
          ?.click();
      }
    }
    echo.onKeyDown(event);
  };

  return (
    <ControlAnchor>
      <Tooltip placement="top" content={fullLabel}>
        <Control $hasError={hasError} $isDisabled={isDisabled} $isSearchable={isSearchable}>
          {shouldShowPrefix && (
            <PrefixContainer>
              <Prefix>{prefix}</Prefix>
            </PrefixContainer>
          )}
          <InputSizer data-value={echo.sizerText} $hasPrefix={shouldShowPrefix}>
            <Input
              ref={echo.inputRef}
              id={inputId}
              // The overlay renders the selection, so the placeholder must not
              // shine through underneath it.
              placeholder={shouldShowOverlay ? undefined : placeholder}
              readOnly={!isSearchable}
              autoFocus={isAutoFocused}
              $isValueHidden={shouldShowOverlay}
              $isCaretHidden={echo.isRichOverlay}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={handleKeyDown}
              onPaste={echo.onPaste}
            />
            {shouldShowOverlay && <ValueOverlay ref={overlayRef}>{echo.overlay}</ValueOverlay>}
          </InputSizer>
          <Indicators>
            {isClearable && hasSelection && !isDisabled && (
              <ClearButton aria-label={t('clear')}>
                <IconCloseRegular />
              </ClearButton>
            )}
            <DropdownButton aria-hidden>
              {isDisabled ? (
                <IconFormIndicatorLocked dataTestid="select-locked-indicator" />
              ) : (
                <IconDropdown />
              )}
            </DropdownButton>
          </Indicators>
        </Control>
      </Tooltip>
    </ControlAnchor>
  );
};
